import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { activeSubscriptionSelector, entitlementUnknownSelector, selectUserObject } from '@common-reducers/UserSelectors';
import { EntitlementErrorDialog } from '@common-components/entitlement-error-dialog/entitlement-error-dialog';
import { DeleteConfirmationDialog } from '@common/components/delete-confirmation-dialog';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { DBSessionThunks } from '@common/reducers/DBServiceThunks';
import { AlertTriangle, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EngineRow, DeployNewEngineButton, ConnectGatewayDialog } from './components';
import { ConnectionGuide } from './components/ConnectionGuide';
import { selectConnectionKind } from '@common-reducers/ConnectedEnginesSlice';
import { useGetFirstWeekChecklistQuery } from '@common-reducers/backend-api/backend-api';
import { useOpenFirstWeekComposer } from '@common/components/first-week-card/use-open-first-week-composer';
import { isAwaitingUserAction, statusHint } from './pairing-connect';
import { useRemoteConnection } from './use-remote-connection';
import { DisplayEngine } from './types';
import { selectDisplayEngines } from './engines-management.selectors';
import { useNowTick } from '@common/hooks/use-now-tick';
import classes from '../../common/views/account-view/account-view.module.scss';
import { isClearFlow } from '@common/utils/clear-flow';
import { ConnectFlow } from '../onboarding/connect-flow';
import { connectSurfaceCta } from '../onboarding/connect-events';
import { messageDoneFrom } from '../onboarding/onboarding-logic';
import { ConnectCard } from '../onboarding/onboarding-ui';
import { useConnectFlow } from '../onboarding/use-connect-flow';

// Note: useTransientInterval removed - no longer needed with MQTT presence updates

/**
 * EnginesManagementPage - Updated for MQTT Presence Architecture
 * 
 * This page now uses MQTT presence channels for real-time engine tracking:
 * - Engine status updates come from MQTT presence messages (not polling)
 * - ConnectedEnginesSlice receives updates from MQTTSubscriber
 * - Session data is still fetched once for engine metadata
 * - Real-time presence updates are handled automatically via MQTT
 */
const CONNECTIONS_LIST_ID = 'bt-connections-list';

export const EnginesManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation(namespace);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserObject);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';
  // FE#159 / FE#167 family — both `hasSocket() ?? false` and `getAllowedEngineCount() ?? 0`
  // below read the unentitled value when the subscription simply has not loaded. That is
  // the same defect on a BLOCKING path: at count 0 the quota dialog says "upgrade to Pro
  // required" and `canAddEngine` is false, so a paying customer is told to buy what they
  // own and cannot add an engine. Hold until entitlement resolves.
  const entitlementUnknown = useAppSelector(entitlementUnknownSelector);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedEngineId, setSelectedEngineId] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [engineToDelete, setEngineToDelete] = useState<DisplayEngine | null>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  const isSessionsLoading = useAppSelector((state) => state.DBSessionReducer.itemsLoading);
  const isSessionsLoaded = useAppSelector((state) => state.DBSessionReducer.itemsLoaded);
  const isInitialLoading = useMemo(() => !isSessionsLoaded && isSessionsLoading, [isSessionsLoaded, isSessionsLoading]);
  const isSessionsError = useMemo(() => !isSessionsLoaded && !isSessionsLoading, [isSessionsLoaded, isSessionsLoading]);

  // Force-refresh tick (shared hook): the lastSeen freshness logic lives in selectDisplayEngines, but a
  // memoized selector won't re-run when only wall-clock time passes (e.g. the pod died and stopped
  // heartbeating). This feeds the selector's `now` so a stale engine flips to 'disconnected'.
  const nowTick = useNowTick();

  const { createRemote, ensureRunning, isCreating } = useRemoteConnection();
  const [deleteK8sEngine, { isLoading: isDeleting }] = backendApi.useDeleteK8sEngineMutation();

  // Get display engines using selector (nowTick drives the lastSeen freshness re-eval)
  const displayEngines = useAppSelector((state) => selectDisplayEngines(state, nowTick));

  // Activation instrumentation for the /connections first-run hero (connect_surface_*).
  const [recordConnectEvent] = backendApi.useRecordConnectEventMutation();
  const heroImpressionRef = useRef(false);
  const fireConnectEvent = useCallback((
    name: 'connect_surface_impression' | 'connect_surface_clicked',
    props?: Record<string, unknown>,
  ) => {
    try {
      recordConnectEvent({ name, props: { surface: 'connections_hero', ...(props ?? {}) } })
        .catch(() => { /* intentionally ignored */ });
    } catch {
      /* never break the page */
    }
  }, [recordConnectEvent]);

  // What the guide at the top shows — all read from existing sources, nothing re-derived:
  // the engine-state selector, the extension's ping answer, the first-week checklist.
  const connection = useAppSelector(selectConnectionKind);
  const { data: firstWeek } = useGetFirstWeekChecklistQuery(undefined, { skip: !user?._id });
  // Unknown until it loads — treat as not yet, so the header button doesn't flash.
  const selfTestDone = messageDoneFrom(firstWeek, false);
  const openComposer = useOpenFirstWeekComposer('web');
  const clearFlow = isClearFlow();


  // A gateway waiting on the user comes first — classified by the pairing flow's own statusHint.
  const attention = useMemo(() => {
    for (const e of displayEngines) {
      // Legacy gateways can't be linked (their row offers no connect action) — never point the user at one.
      if (e.type !== 'gateway' || e.isLegacy) continue;
      if (isAwaitingUserAction(e.status)) return { engineId: e.id, reason: 'scan' as const };
      if (statusHint(e.status) === 'hintDisconnected') return { engineId: e.id, reason: 'offline' as const };
    }
    return undefined;
  }, [displayEngines]);

  // The connect wizard (the same one /onboarding shows) owns the card until the user is set up —
  // connected AND first message out. It also loads sessions and the plan on mount.
  const onConnectEvent = useCallback((name: string, props?: Record<string, unknown>) => {
    const cta = connectSurfaceCta(name, props);
    if (cta) fireConnectEvent('connect_surface_clicked', { cta });
  }, [fireConnectEvent]);
  const connectModel = useConnectFlow({
    surface: 'connections',
    onEvent: onConnectEvent,
    // At the gateway limit: the connection to reconnect is in the list right below.
    onManage: () => document.getElementById(CONNECTIONS_LIST_ID)?.scrollIntoView({ behavior: 'smooth' }),
    onAlwaysOnPro: () => handleCreateEngineClickRef.current(),
  });
  // The wizard owns the card until the user is set up (connected + first message out), and keeps
  // its Done screen when that happened during this visit. While it settles it shows its own spinner.
  const { screen } = connectModel.flow;
  const wizard = !clearFlow && (!connectModel.ready || screen !== 'done' || connectModel.message.deliveredHere);

  // Hero impression: once per entry into the not-connected part of the wizard — only after the
  // state settled, so an already-connected user whose presence lands late isn't counted.
  const heroVisible = !isInitialLoading && !isSessionsError && wizard && connectModel.ready && (screen === 'choose' || screen === 'local' || screen === 'remote');
  useEffect(() => {
    if (!heroVisible) { heroImpressionRef.current = false; return undefined; }
    if (heroImpressionRef.current) return undefined;
    const id = window.setTimeout(() => {
      heroImpressionRef.current = true;
      fireConnectEvent('connect_surface_impression');
    }, 1500);
    return () => window.clearTimeout(id);
  }, [heroVisible, fireConnectEvent]);

  // The header's "add always-on" button is only for adding ANOTHER always-on connection,
  // once onboarding is past its first test. Until then the guide owns the next move
  // (connect, test, offer always-on, or fix the existing gateway) and a second primary
  // button would compete with it.
  const showDeployButton = connection === 'remote' && !attention && selfTestDone;

  // Engine count validation
  const allowedEngineCount = useMemo(() => {
    return activeSubscription?.getAllowedEngineCount() ?? 0;
  }, [activeSubscription]);

  const currentEngineCount = useMemo(() => {
    // Count only gateway engines owned by the user (excluding legacy)
    return displayEngines.filter(engine =>
      engine.type === 'gateway' && !engine.isLegacy
    ).length;
  }, [displayEngines]);

  const canCreateEngine = useMemo(() => {
    return user?._id && allowedEngineCount > 0 && currentEngineCount < allowedEngineCount;
  }, [user?._id, allowedEngineCount, currentEngineCount]);


  // Note: Session status updates now come from MQTT presence messages
  // No need for polling-based refresh - the MQTTSubscriber handles real-time updates

  // The engine the connect dialog is bound to. Recomputed from the live displayEngines so its
  // `.session` (status / pairingCode) stays fresh while the dialog is open.
  const selectedEngine = useMemo(
    () => displayEngines.find(e => e.id === selectedEngineId),
    [selectedEngineId, displayEngines],
  );

  const handleCreateEngineClick = () => {
    if (!canCreateEngine) {
      setQuotaDialogOpen(true);
      return;
    }
    handleCreateEngine();
  };
  // Latest-closure handle so the memoized "start over" (expired QR) always creates with fresh state.
  const handleCreateEngineClickRef = useRef(handleCreateEngineClick);
  handleCreateEngineClickRef.current = handleCreateEngineClick;

  const handleCreateEngine = async () => {
    // Security check + engine count validation
    if (!user?._id || !canCreateEngine) {
      console.error(user?._id ? t('enginesManagement.engineLimitReached') : t('enginesManagement.unauthorizedCreate'));
      return;
    }
    // Open the connect flow as soon as the session exists, so the user watches it go
    // Prepare → Scan → Link instead of hunting for a button on the row.
    await createRemote((sessionId) => {
      setSelectedEngineId(sessionId);
      setConnectDialogOpen(true);
    });
  };

  // Stable identities so memoized <EngineRow>s don't re-render when the parent
  // re-renders on the 30s `now` tick.
  // One entry point: the dialog picks the method (QR on desktop, phone-linking on mobile).
  // A decommissioned gateway (WhatsApp unlinked it → deployment + pod torn down, session kept as
  // 'disconnected' for re-link) has nothing running that could ever produce a QR — opening the
  // dialog alone just spun on "Preparing" forever. Start an engine for it first; the dialog then
  // shows the real Prepare → Scan flow.
  const handleConnect = useCallback((engineId: string) => {
    setSelectedEngineId(engineId);
    setConnectDialogOpen(true);
    ensureRunning(displayEngines.find(e => e.id === engineId)?.session);
  }, [displayEngines, ensureRunning]);

  const handleDeleteEngine = useCallback((engine: DisplayEngine) => {
    setEngineToDelete(engine);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!engineToDelete) return;

    try {
      if (engineToDelete.session) {
        // Tear down the k8s engine resources (Deployment + PVC).
        await deleteK8sEngine(engineToDelete.session._id).unwrap();
        // Delete the session record via the DB-slice thunk so the row is
        // optimistically removed from the store immediately. The previous
        // removeSession RTK mutation + DBSessionThunks.find({}) refetch never
        // dropped it: a `find` upserts into the entity adapter and cannot
        // remove an entity that's absent from the result, so the deleted
        // connection lingered until a full page refresh (which wipes the store).
        await dispatch(DBSessionThunks.delete({ entity: engineToDelete.session })).unwrap();
      }
    } catch (error) {
      console.error(t('enginesManagement.deleteEngineError'), error);
    } finally {
      setDeleteDialogOpen(false);
      setEngineToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEngineToDelete(null);
  };

  // If the dialog's engine disappears while it is open, the server reaped it — for a gateway still
  // waiting on a scan that means the QR attempts ran out. Keep the dialog up on its last known
  // engine and tell the user, instead of silently closing (or, before the store pruned reaped
  // sessions, leaving a dead QR on screen).
  const lastSelectedEngineRef = useRef<DisplayEngine | undefined>(undefined);
  if (selectedEngine) lastSelectedEngineRef.current = selectedEngine;
  const lastSelectedEngine = lastSelectedEngineRef.current?.id === selectedEngineId ? lastSelectedEngineRef.current : undefined;
  const connectEngineGone = connectDialogOpen && !!selectedEngineId && !selectedEngine && !!lastSelectedEngine;

  const handleStartOver = useCallback(() => {
    setConnectDialogOpen(false);
    setSelectedEngineId('');
    handleCreateEngineClickRef.current();
  }, []);

  // The list is its own card under the guide / wizard — a separate thing, with its own title.
  const connectionsList = displayEngines.length > 0 ? (
    <div id={CONNECTIONS_LIST_ID} style={{ marginTop: 16 }}>
      <ConnectCard dir={isRtl ? 'rtl' : 'ltr'}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, letterSpacing: '-.01em', color: 'var(--color-text)' }}>
          {t('enginesManagement.myConnections', { defaultValue: 'My WhatsApp Connections' })}
        </h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('enginesManagement.engine')}</TableHead>
              <TableHead>{t('enginesManagement.status')}</TableHead>
              <TableHead>{t('enginesManagement.connection')}</TableHead>
              <TableHead>{t('enginesManagement.lastSeen')}</TableHead>
              <TableHead className="w-px" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayEngines.map((engine) => (
              <EngineRow
                key={engine.id}
                engine={engine}
                onConnect={handleConnect}
                onDelete={handleDeleteEngine}
              />
            ))}
          </TableBody>
        </Table>
      </ConnectCard>
    </div>
  ) : null;

  return (
    <div className={classes.pageContainer}>
      {/* No page title: the guide card below already says where you are, and the list is
          titled "My WhatsApp Connections". The header row only exists to hold the add button. */}
      {showDeployButton && (
        <div className={classes.titleContainer}>
          <div className="ms-auto">
            <DeployNewEngineButton
              onClick={handleCreateEngineClick}
              isLoading={isCreating}
            />
          </div>
        </div>
      )}

      <div className={classes.accountSettingBody} style={{ paddingTop: 12 }}>
        <div className="space-y-6">
        {!isInitialLoading && !isSessionsError && (wizard ? (
          <ConnectCard dir={isRtl ? 'rtl' : 'ltr'}>
            <ConnectFlow model={connectModel} wide />
          </ConnectCard>
        ) : (
          <ConnectionGuide
            kind={connection}
            attention={attention}
            selfTestDone={selfTestDone}
            isCreating={isCreating}
            onGoAlwaysOn={handleCreateEngineClick}
            onFixGateway={handleConnect}
            // The web composer lives on the calendar: seed it, then take them there.
            onSendTest={() => { openComposer('self_test'); navigate('/calendar'); }}
            onTrack={(cta) => fireConnectEvent('connect_surface_clicked', { cta })}
          />
        ))}
        {!isInitialLoading && !isSessionsError && connectionsList}

        {isInitialLoading && (
          <div className="h-60 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        )}

        {isSessionsError && (
          <Alert variant="destructive">
            <AlertTitle>{t('enginesManagement.errorLoadingEngines')}</AlertTitle>
            <AlertDescription>
              {t('enginesManagement.errorLoadingEnginesDescription')}
            </AlertDescription>
          </Alert>
        )}


        {/* Connect Dialog — QR + "link with phone number" (pairing code), #302 */}
        <ConnectGatewayDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          engine={selectedEngine ?? lastSelectedEngine}
          expired={connectEngineGone}
          onStartOver={handleStartOver}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title={t('enginesManagement.deleteEngineTitle')}
          itemToDelete={engineToDelete?.name}
          loading={isDeleting}
        />

        {/* FE#167 — entitlement fetch gave up; explain rather than leave the quota
            controls inert with no reason. Shows only while the page is actually held. */}
        <EntitlementErrorDialog open={entitlementUnknown} />

        {/* Engine Quota Dialog */}
        <Dialog open={quotaDialogOpen} onOpenChange={setQuotaDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" dir={isRtl ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" style={{ color: 'var(--status-warning)' }} />
                {entitlementUnknown
                  ? t('enginesManagement.engineLimitReached')
                  : allowedEngineCount === 0
                    ? t('enginesManagement.upgradeToProRequired')
                    : t('enginesManagement.engineLimitReached')}
              </DialogTitle>
              <DialogDescription className="space-y-3 pt-2">
                {!entitlementUnknown && allowedEngineCount === 0 ? (
                  <div className="space-y-3">
                    <p>
                      {t('enginesManagement.upgradeToProDescription')}
                    </p>
                    <p>
                      {t('enginesManagement.enginePerAccountInfo')}
                    </p>
                    <div className="flex gap-2 pt-2">
                      {/* Upgrade sends the user to /account-subscription, a full-Blueticks
                          page, and the checkout beyond it is Blueticks-branded. The clear
                          flow has no subscription surface of its own yet, so it shows no
                          upgrade CTA at all rather than leaking the other brand. */}
                      {!isClearFlow() && (
                        <Button
                          onClick={() => navigate('/account-subscription')}
                          className="relative overflow-hidden transition-all duration-300 ease-out hover:shadow-xl active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-300/50 text-white group"
                          style={{
                            background: 'var(--remote-connect-bg)',
                            boxShadow: '0 4px 15px rgba(var(--remote-connect-shadow-rgb), 0.3)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--remote-connect-hover-bg)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(var(--remote-connect-shadow-rgb), 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--remote-connect-bg)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(var(--remote-connect-shadow-rgb), 0.3)';
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.background = 'var(--remote-connect-active-bg)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(var(--remote-connect-shadow-rgb), 0.5)';
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.background = 'var(--remote-connect-hover-bg)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(var(--remote-connect-shadow-rgb), 0.4)';
                          }}
                        >
                          {t('enginesManagement.upgradeToPro')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setQuotaDialogOpen(false)}
                      >
                        {t('enginesManagement.maybeLater')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p>
                      {t('enginesManagement.engineLimitReachedMessage', { count: allowedEngineCount })}
                    </p>
                    <p>
                      {t('enginesManagement.engineLimitExpanding')}
                    </p>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setQuotaDialogOpen(false)}
                      >
                        {t('enginesManagement.gotIt')}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  </div>
  );
};
